import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CornerDownLeft, History, Loader2, Search, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, unwrapObject } from "@/lib/api";
import { cn } from "@/lib/utils";
import { listHref, TYPE_INFO, type SearchHit, type SearchResponse, type SearchType } from "@/lib/searchTypes";

/** Words that narrow the search to one kind of record: "order: urban" looks only in orders */
const PREFIXES: Array<{ word: string; type: SearchType; hint: string }> = [
  { word: "order", type: "order", hint: "Search orders" },
  { word: "quote", type: "quote", hint: "Search quotes" },
  { word: "dispatch", type: "dispatch", hint: "Search dispatches (LR, vehicle, e-way bill)" },
  { word: "wo", type: "workorder", hint: "Search work orders" },
  { word: "jw", type: "challan", hint: "Search job-work challans" },
  { word: "product", type: "product", hint: "Search products and shades" },
  { word: "roll", type: "roll", hint: "Search stock rolls and barcodes" },
  { word: "lot", type: "roll", hint: "Search rolls by dye lot" },
  { word: "supplier", type: "supplier", hint: "Search suppliers" },
  { word: "grn", type: "receipt", hint: "Search goods receipts" },
  { word: "qc", type: "qc", hint: "Search inspections" },
  { word: "buyer", type: "account", hint: "Search buyers (accounts)" },
  { word: "deal", type: "deal", hint: "Search deals" },
  { word: "lead", type: "lead", hint: "Search leads" },
  { word: "contact", type: "contact", hint: "Search contacts" },
];

/** Screens that can be opened by name */
const PAGES: Array<{ label: string; to: string; permission?: string; keywords?: string }> = [
  { label: "Dashboard", to: "/dashboard" },
  { label: "Sales pipeline", to: "/deals", permission: "deal.read", keywords: "deals" },
  { label: "Leads", to: "/leads", permission: "lead.read" },
  { label: "Samples", to: "/samples", permission: "sample.read" },
  { label: "Quotes", to: "/quotes", permission: "quote.read" },
  { label: "Orders & dispatch", to: "/orders", permission: "order.read", keywords: "sales orders" },
  { label: "Accounts (buyers)", to: "/accounts", permission: "account.read", keywords: "buyers customers" },
  { label: "Contacts", to: "/contacts", permission: "contact.read" },
  { label: "Complaints", to: "/complaints", permission: "complaint.read" },
  { label: "Products", to: "/products", permission: "product.read", keywords: "catalogue" },
  { label: "Stock", to: "/inventory", permission: "inventory.read", keywords: "inventory rolls" },
  { label: "Goods receipts", to: "/inventory/receipts", permission: "inventory.read", keywords: "grn" },
  { label: "Warehouses", to: "/inventory/warehouses", permission: "inventory.read" },
  { label: "Quality inspections", to: "/inventory/quality", permission: "inventory.read", keywords: "qc" },
  { label: "Stock reports", to: "/inventory/reports", permission: "inventory.read", keywords: "ageing fast slow" },
  { label: "Suppliers", to: "/suppliers", permission: "inventory.read" },
  { label: "Production board", to: "/production", permission: "production.read", keywords: "work orders" },
  { label: "Wastage report", to: "/production/report", permission: "production.read", keywords: "yield performance" },
  { label: "Production processes", to: "/settings/processes", permission: "production.read" },
  { label: "Tasks", to: "/tasks" },
  { label: "Calendar", to: "/calendar" },
  { label: "Emails", to: "/emails" },
  { label: "WhatsApp", to: "/whatsapp" },
  { label: "Reports", to: "/reports", permission: "report.view_own" },
];

const RECENTS_KEY = "crm.search.recents";
const MAX_RECENTS = 6;
const readRecents = (): SearchHit[] => {
  try {
    const list = JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]");
    return Array.isArray(list) ? list.slice(0, MAX_RECENTS) : [];
  } catch {
    return [];
  }
};
const saveRecent = (hit: SearchHit) => {
  try {
    const rest = readRecents().filter((r) => !(r.type === hit.type && r.id === hit.id));
    localStorage.setItem(RECENTS_KEY, JSON.stringify([hit, ...rest].slice(0, MAX_RECENTS)));
  } catch {
    /* private mode: recents just aren't kept */
  }
};

interface Row {
  key: string;
  icon: typeof Search;
  title: string;
  detail?: string;
  /** shown on the right instead of the arrow */
  tag?: string;
  run: () => void;
}
interface Section {
  title: string;
  rows: Row[];
}

/** The search panel: opens over the page with Ctrl/Cmd+K, finds records and screens, remembers what you opened */
export function CommandSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState<SearchResponse["groups"]>([]);
  const [jump, setJump] = useState<SearchHit | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [partial, setPartial] = useState(false);
  const [active, setActive] = useState(0);
  const [recents, setRecents] = useState<SearchHit[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);

  // "order: urban" narrows to one kind of record
  const parsed = useMemo(() => {
    const m = query.match(/^\s*([a-z]+)\s*:\s*(.*)$/i);
    const p = m ? PREFIXES.find((x) => x.word === m[1]!.toLowerCase()) : undefined;
    return p ? { type: p.type as SearchType | "all", text: (m![2] || "").trim() } : { type: "all" as const, text: query.trim() };
  }, [query]);
  const textLength = [...parsed.text].length;

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setGroups([]);
    setJump(null);
    setActive(0);
    setRecents(readRecents());
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (textLength < 2) {
      requestId.current += 1;
      setGroups([]);
      setJump(null);
      setLoading(false);
      setFailed(false);
      setPartial(false);
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: parsed.text, type: parsed.type, page_size: parsed.type === "all" ? "3" : "8" });
        const res = unwrapObject<SearchResponse>(await api.get(`/api/Search?${params}`));
        if (id !== requestId.current) return;
        setGroups((res.groups || []).filter((g) => g.items.length > 0));
        setJump(res.jump || null);
        setPartial(Boolean(res.failed_types?.length));
        setFailed(false);
      } catch {
        if (id !== requestId.current) return;
        setGroups([]);
        setJump(null);
        setFailed(true);
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    }, 220);
    return () => window.clearTimeout(timer);
  }, [open, parsed.text, parsed.type, textLength]);

  const go = (hit: SearchHit) => {
    saveRecent(hit);
    onClose();
    navigate(hit.href);
  };
  const toRow = (hit: SearchHit): Row => ({ key: `${hit.type}:${hit.id}`, icon: TYPE_INFO[hit.type].icon, title: hit.title, detail: hit.detail, run: () => go(hit) });

  const sections: Section[] = useMemo(() => {
    const out: Section[] = [];
    if (textLength < 2) {
      const typed = query.trim().toLowerCase();
      if (!typed) {
        if (recents.length) out.push({ title: "Recents", rows: recents.filter((r) => TYPE_INFO[r.type] && hasPermission(TYPE_INFO[r.type].permission)).map((r) => ({ ...toRow(r), tag: TYPE_INFO[r.type].title.replace(/s$/, "") })) });
      }
      // Eight of the most useful shortcuts until a prefix is being typed
      const TOP = ["order", "quote", "dispatch", "wo", "product", "roll", "buyer", "supplier"];
      const tips = PREFIXES.filter((p) => (typed ? p.word.startsWith(typed.replace(/:.*/, "")) : TOP.includes(p.word))).filter((p) => hasPermission(TYPE_INFO[p.type].permission));
      if (tips.length) out.push({ title: "Search tips", rows: tips.map((p) => ({ key: `tip:${p.word}`, icon: Zap, title: `${p.word}:`, detail: p.hint, run: () => { setQuery(`${p.word}: `); window.setTimeout(() => inputRef.current?.focus(), 0); } })) });
      const pages = typed ? PAGES.filter((p) => (!p.permission || hasPermission(p.permission)) && `${p.label} ${p.keywords || ""}`.toLowerCase().includes(typed)) : [];
      if (pages.length) out.push({ title: "Go to", rows: pages.slice(0, 5).map((p) => ({ key: `page:${p.to}`, icon: ArrowRight, title: p.label, detail: "Open screen", run: () => { onClose(); navigate(p.to); } })) });
      return out;
    }
    // A whole document number or barcode that is one record comes first
    if (jump) out.push({ title: "Best match", rows: [toRow(jump)] });
    for (const g of groups) {
      const rows = g.items.filter((i) => !(jump && i.type === jump.type && i.id === jump.id)).map(toRow);
      // A group with more matches than the panel shows ends with a way into that screen, already searched
      if (g.has_more) rows.push({ key: `more:${g.type}`, icon: ArrowRight, title: `More ${TYPE_INFO[g.type].title.toLowerCase()}`, detail: `open ${TYPE_INFO[g.type].title.toLowerCase()} for “${parsed.text}”`, run: () => { onClose(); navigate(listHref(g.type, parsed.text)); } });
      if (rows.length) out.push({ title: TYPE_INFO[g.type].title, rows });
    }
    if (parsed.type === "all") {
      const q = parsed.text.toLowerCase();
      const pages = PAGES.filter((p) => (!p.permission || hasPermission(p.permission)) && `${p.label} ${p.keywords || ""}`.toLowerCase().includes(q)).slice(0, 3);
      if (pages.length) out.push({ title: "Go to", rows: pages.map((p) => ({ key: `page:${p.to}`, icon: ArrowRight, title: p.label, detail: "Open screen", run: () => { onClose(); navigate(p.to); } })) });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, jump, recents, query, parsed.text, parsed.type, textLength, hasPermission]);

  const flat = sections.flatMap((s) => s.rows);
  useEffect(() => setActive(0), [sections]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-row="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); if (flat.length) setActive((i) => (i + 1) % flat.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); if (flat.length) setActive((i) => (i <= 0 ? flat.length - 1 : i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); flat[active]?.run(); }
  };

  if (!open) return null;
  let index = -1;
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-slate-900/40 px-4 pt-[10vh] backdrop-blur-[2px]" onMouseDown={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Search" className="flex max-h-[78vh] w-full max-w-[660px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-2xl" onMouseDown={(e) => e.stopPropagation()} onKeyDown={onKeyDown}>
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-slate-200 px-4">
          <Search className="size-5 shrink-0 text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            maxLength={100}
            placeholder="Search buyers, orders, products, rolls and screens…"
            aria-label="Search"
            className="h-full min-w-0 flex-1 bg-transparent text-[15px] text-slate-900 outline-none placeholder:text-slate-400"
          />
          {loading && <Loader2 className="size-4 shrink-0 animate-spin text-slate-400" />}
          <kbd className="shrink-0 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">Esc</kbd>
        </div>

        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-2 py-2" role="listbox" aria-label="Results">
          {failed && <p className="px-3 py-3 text-sm text-rose-600">Search is unavailable right now. Please try again.</p>}
          {!failed && partial && textLength >= 2 && <p className="px-3 py-1.5 text-xs text-amber-700">Some record types could not be searched.</p>}
          {!failed && textLength >= 2 && !loading && !groups.length && !flat.some((r) => r.key.startsWith("page:")) && (
            <p className="px-3 py-3 text-sm text-slate-500">No records match “{parsed.text}”. Try a name, an order number, a roll barcode, or a product code.</p>
          )}
          {sections.map((s, si) => (
            <div key={`${s.title}:${si}`} className={s.title ? "pb-1" : "mt-1 border-t border-slate-100 pt-1"}>
              {s.title && <div className="px-3 pb-1 pt-2 text-xs font-semibold text-slate-500">{s.title}</div>}
              {s.rows.map((r) => {
                index += 1;
                const i = index;
                const Icon = s.title === "Recents" ? History : r.icon;
                return (
                  <button
                    key={r.key}
                    type="button"
                    role="option"
                    aria-selected={active === i}
                    data-row={i}
                    onMouseMove={() => active !== i && setActive(i)}
                    onClick={r.run}
                    className={cn("flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors", active === i ? "bg-slate-100" : "hover:bg-slate-50")}
                  >
                    <Icon className="size-[18px] shrink-0 text-slate-500" />
                    <span className="min-w-0 flex-1 truncate text-sm">
                      <span className="font-medium text-slate-900">{r.title}</span>
                      {r.detail && <span className="text-slate-500"> — {r.detail}</span>}
                    </span>
                    {r.tag ? <span className="shrink-0 text-xs text-slate-400">{r.tag}</span> : active === i ? <ArrowRight className="size-4 shrink-0 text-slate-500" /> : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex shrink-0 items-center gap-4 border-t border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5">↑</kbd><kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5">↓</kbd> to navigate</span>
          <span className="flex items-center gap-1.5"><kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5"><CornerDownLeft className="inline size-3" /></kbd> to select</span>
          <span className="ml-auto hidden truncate sm:block">Type a whole order number or roll barcode to open it</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
